import {
  type ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import {
  dropdownMenuContentClassname,
  dropdownMenuItemVariants,
  dropdownMenuListClassname,
} from "common/components/ui/DropdownMenu";
import type { EditorVariable } from "common/types";

interface VariableListProps {
  items: { value: string; name: string }[];
  command: (item: EditorVariable) => void;
  customVariables: { value: string; name: string }[];
}

interface VariableListRef {
  onKeyDown: (params: { event: KeyboardEvent }) => boolean;
}

function VariableList(
  { items, command, customVariables }: VariableListProps,
  ref: ForwardedRef<VariableListRef>,
) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  function selectItem(index: number) {
    const item = items[index];

    const isCustomVar = !!customVariables.find(
      (customVar) => customVar.value === item.value,
    );

    if (item) {
      command({
        id: item.value,
        label: item.name,
        isCustomVar,
        fallback: "",
      });
    }
  }

  function upHandler() {
    setSelectedIndex(
      (prevIndex) => (prevIndex + items.length - 1) % items.length,
    );
  }

  function downHandler() {
    setSelectedIndex((prevIndex) => (prevIndex + 1) % items.length);
  }

  function enterHandler() {
    selectItem(selectedIndex);
  }

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <div className={dropdownMenuContentClassname}>
      <div className={dropdownMenuListClassname}>
        {items.length ? (
          items.map(({ value, name }, index) => {
            const isSelected = index === selectedIndex;
            const isCustomVar = !!customVariables.find(
              (customVar) => customVar.value === value,
            );
            return (
              <button
                key={value}
                type="button"
                className={dropdownMenuItemVariants({
                  variant: isCustomVar ? "blue" : "default",
                  size: "md",
                  className:
                    isSelected && (isCustomVar ? "bg-blue-50" : "bg-black-50"),
                })}
                onFocus={() => setSelectedIndex(index)}
                onMouseOver={() => setSelectedIndex(index)}
                onClick={() => selectItem(index)}
              >
                {name}
              </button>
            );
          })
        ) : (
          <p className="p-4 text-center text-black-500">No result</p>
        )}
      </div>
    </div>
  );
}

export default forwardRef(VariableList);
